import React, { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { useWidgetLayout } from "../WidgetLayoutContext";
import { AHText, Box, Circle, useLayoutContext, useScaleFactor } from "arkh";
import { WidgetTeamScheduleSectionHeaderStatusDot } from "./WidgetTeamScheduleSectionHeaderStatusDot";
import { RoutineOccasionStatus } from "./__generated__/WidgetTeamScheduleInlineRoutineListItemMutation.graphql";
import { WidgetTeamScheduleContentStatus_teamSchedule$key } from "./__generated__/WidgetTeamScheduleContentStatus_teamSchedule.graphql";

export function WidgetTeamScheduleContentStatus(props: {
  teamSchedule: WidgetTeamScheduleContentStatus_teamSchedule$key;
}) {
  const teamSchedule = useFragment(
    graphql`
      fragment WidgetTeamScheduleContentStatus_teamSchedule on TeamSchedule {
        events(includeInlineRoutines: true) {
          id
          ... on RoutineOccasion {
            __typename
            status(calculateNoTasks: true, calculateNotCompletedStarted: true)
            ...WidgetTeamScheduleSectionHeaderStatusDot_routineOccasion
          }
          ... on TeamScheduleInlineRoutineOccasion {
            __typename
            taskExecution {
              routineOccasion {
                status(
                  calculateNoTasks: true
                  calculateNotCompletedStarted: true
                )
                __typename
                ...WidgetTeamScheduleSectionHeaderStatusDot_routineOccasion
              }
            }
          }
        }
      }
    `,
    props.teamSchedule
  );

  const events = useMemo(() => teamSchedule.events, [teamSchedule.events]);

  const renderStatusDot = useCallback((item: (typeof events)[number]) => {
    const occasion =
      item.__typename === "RoutineOccasion"
        ? item
        : item.taskExecution?.routineOccasion;

    if (!occasion) {
      return [];
    }

    return [
      <Box insets={{ left: 2 }} key={item.id}>
        <WidgetTeamScheduleSectionHeaderStatusDot occasion={occasion} />
      </Box>,
    ];
  }, []);

  const renderStatusCircle = useCallback((status: RoutineOccasionStatus) => {
    switch (status) {
      case "NOT_COMPLETED_STARTED":
        return <Circle size="dot" color="semiNegative" />;
      case "FUTURE":
        return <Circle size="dot" borderColor="neutral" borderWidth={1} />;
      case "REQUIRED_COMPLETED":
      case "COMPLETED":
        return <Circle size="dot" color="positive" />;
      case "NOT_COMPLETED":
        return <Circle size="dot" color="negative" />;
      case "IN_PROGRESS":
        return <Circle size="dot" borderColor="primary" borderWidth={1} />;
      case "IN_PROGRESS_EXPIRED":
        return <Circle size="dot" color="negative" />;
      case "NO_TASKS_COMPLETED":
        return <Circle size="dot" color="border-primary" />;
      default:
        return <Circle size="dot" color="surface" />;
    }
  }, []);

  const summary = new Map();
  events.forEach((item) => {
    const occasion =
      item.__typename === "RoutineOccasion"
        ? item
        : item.taskExecution?.routineOccasion;

    if (!occasion || !occasion.status) {
      return;
    }
    summary.set(occasion.status, (summary.get(occasion.status) ?? 0) + 1);
  });

  const summaryList = (
    [
      ["NOT_COMPLETED", "IN_PROGRESS_EXPIRED"],
      ["COMPLETED", "REQUIRED_COMPLETED"],
      ["IN_PROGRESS"],
      ["FUTURE"],
      ["NO_TASKS_COMPLETED"],
    ] as RoutineOccasionStatus[][]
  ).flatMap((status) => {
    const count = status.reduce((acc, cur) => acc + (summary.get(cur) ?? 0), 0);
    if (!count) {
      return [];
    }
    return [{ status: status[0], count }];
  });

  const layout = useLayoutContext();
  const { width } = useWidgetLayout();

  const sf = useScaleFactor();
  if (
    events.length * (layout.circle.dot + 2) >
    width - 90 * sf - layout.space.medium
  ) {
    return (
      <Box
        style={{
          flexDirection: "row",
          flex: 1,
          justifyContent: "flex-end",
        }}
      >
        {summaryList.map(({ status, count }) => (
          <Box
            key={status}
            spaceLeft="xsmall"
            style={{ flexDirection: "row", alignItems: "center" }}
          >
            {renderStatusCircle(status)}
            <AHText spaceLeft="xsmall" type="subtitle">
              {count}
            </AHText>
          </Box>
        ))}
      </Box>
    );
  } else {
    return (
      <Box
        style={{
          flexDirection: "row",
          flex: 1,
          justifyContent: "flex-end",
        }}
      >
        {events.map(renderStatusDot)}
      </Box>
    );
  }
}
